import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { subDays, subMonths, format } from 'date-fns';
import supplierService from '../../services/supplierService';
import { FullWidthSection, ContentWrapper } from '../../components/ui/Layout';
import KPICards from './components/KPICards';
import RevenueTrendsChart from './components/RevenueTrendsChart';
import OrderConversionChart from './components/OrderConversionChart';
import PopularBeansChart from './components/PopularBeansChart';
import GeographicDistributionMap from './components/GeographicDistributionMap';
import BuyerFeedbackSection from './components/BuyerFeedbackSection';
import ResponseMetricsCard from './components/ResponseMetricsCard';
import BusinessInsights from './components/BusinessInsights';
import { MessagesTab } from '../buyer-dashboard/components/MessagesTab';
import { useLanguage } from '../../contexts/LanguageContext';

const SupplierDashboard = () => {
  const { language } = useLanguage();
  const [dateRange, setDateRange] = useState('30days');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');

  const [loading, setLoading] = useState(true);
  const [supplier, setSupplier] = useState(null);
  const [metrics, setMetrics] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState('analytics');

  useEffect(() => {
    loadSupplierData();
  }, []);

  useEffect(() => {
    if (supplier) {
      loadMetrics();
    }
  }, [dateRange, customStartDate, customEndDate, supplier]);

  const content = {
    en: {
      title: 'Supplier Dashboard',
      subtitle: 'Comprehensive analytics and performance monitoring for your coffee business',
      dateRange: 'Date Range',
      last30Days: 'Last 30 Days',
      lastQuarter: 'Last Quarter',
      lastYear: 'Last Year',
      custom: 'Custom Range',
      startDate: 'Start Date',
      endDate: 'End Date',
      exportCSV: 'Export CSV',
      exportPDF: 'Export PDF',
      loading: 'Loading dashboard...',
      noSupplier: 'No Supplier Account Found',
      noSupplierMessage: 'You do not have a supplier account. Please complete the supplier registration process.',
      backToHome: 'Back to Home',
      backToRegistration: 'Go to Supplier Registration',
      analytics: 'Analytics',
      messages: 'Messages'
    },
    zh: {
      title: '供应商仪表板',
      subtitle: '全面的分析和性能监控，为您的咖啡业务提供支持',
      dateRange: '日期范围',
      last30Days: '最近30天',
      lastQuarter: '最近一季度',
      lastYear: '最近一年',
      custom: '自定义范围',
      startDate: '开始日期',
      endDate: '结束日期',
      exportCSV: '导出CSV',
      exportPDF: '导出PDF',
      loading: '加载仪表板中...',
      noSupplier: '未找到供应商资料。请联系支持。',
      backToHome: '返回首页',
      analytics: '分析',
      messages: '消息'
    }
  };

  const t = content?.[language];

  const loadSupplierData = async () => {
    try {
      setLoading(true);
      const supplierData = await supplierService?.getCurrentSupplier();
      if (!supplierData) {
        setError('No supplier account found. Please complete the supplier registration process.');
        setSupplier(null);
      } else {
        setSupplier(supplierData);
        setError(null);
      }
    } catch (error) {
      console.error('Failed to load supplier:', error);
      setError('Failed to load supplier data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const loadMetrics = async () => {
    try {
      const { start, end } = getDateRangeValues();
      const metricsData = await supplierService?.calculateSupplierMetrics(
        supplier?.id,
        format(start, 'yyyy-MM-dd'),
        format(end, 'yyyy-MM-dd')
      );
      setMetrics(metricsData);
    } catch (error) {
      console.error('Failed to load metrics:', error);
    }
  };

  const getDateRangeValues = () => {
    const today = new Date();
    switch (dateRange) {
      case '30days':
        return { start: subDays(today, 30), end: today };
      case 'quarter':
        return { start: subMonths(today, 3), end: today };
      case 'year':
        return { start: subMonths(today, 12), end: today };
      case 'custom':
        return {
          start: customStartDate ? new Date(customStartDate) : subDays(today, 30),
          end: customEndDate ? new Date(customEndDate) : today
        };
      default:
        return { start: subDays(today, 30), end: today };
    }
  };

  const handleExportCSV = async () => {
    setExporting(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 1000));
      console.log('Exporting CSV...');
    } catch (error) {
      console.error('Export failed:', error);
    } finally {
      setExporting(false);
    }
  };

  const handleExportPDF = async () => {
    setExporting(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 1000));
      console.log('Exporting PDF...');
    } catch (error) {
      console.error('Export failed:', error);
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Icon name="Loader2" size={48} className="animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">{t?.loading}</p>
        </div>
      </div>
    );
  }

  if (!supplier) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="pt-[60px]">
          <FullWidthSection padding="" py="py-8">
            <ContentWrapper maxWidth="6xl" padding="px-5">
              <div className="text-center py-12">
                <Icon name="AlertCircle" size={64} className="text-destructive mx-auto mb-4" />
                <h2 className="text-2xl font-bold text-foreground mb-2">{t?.noSupplier}</h2>
                <p className="text-muted-foreground">{t?.noSupplierMessage}</p>
                <Button
                  variant="outline"
                  iconName="ArrowLeft"
                  onClick={() => navigate('/')}
                  className="mt-4"
                >
                  {t?.backToHome}
                </Button>
                <Button
                  variant="outline"
                  iconName="UserPlus"
                  onClick={() => navigate('/supplier-registration')}
                  className="mt-4"
                >
                  {t?.backToRegistration}
                </Button>
              </div>
            </ContentWrapper>
          </FullWidthSection>
        </main>
      </div>
    );
  }

  const { start, end } = getDateRangeValues();

  const tabs = [
    { id: 'analytics', label: t?.analytics, icon: 'BarChart3' },
    { id: 'messages', label: t?.messages, icon: 'MessageCircle' },
    { id: 'notifications', label: t?.notifications, icon: 'Bell' }
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'analytics':
        return (
          <>
            {/* Date Range Filter */}
            <div className="bg-card border border-border rounded-lg p-6 mb-6">
              <h3 className="text-sm font-medium text-foreground mb-4">{t?.dateRange}</h3>
              <div className="flex flex-wrap gap-3">
                <button
                  onClick={() => setDateRange('30days')}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-smooth ${
                    dateRange === '30days' ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:bg-muted/80'
                  }`}
                >
                  {t?.last30Days}
                </button>
                <button
                  onClick={() => setDateRange('quarter')}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-smooth ${
                    dateRange === 'quarter' ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:bg-muted/80'
                  }`}
                >
                  {t?.lastQuarter}
                </button>
                <button
                  onClick={() => setDateRange('year')}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-smooth ${
                    dateRange === 'year' ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:bg-muted/80'
                  }`}
                >
                  {t?.lastYear}
                </button>
                <button
                  onClick={() => setDateRange('custom')}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-smooth ${
                    dateRange === 'custom' ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:bg-muted/80'
                  }`}
                >
                  {t?.custom}
                </button>
                {dateRange === 'custom' && (
                  <div className="flex gap-3 ml-4">
                    <div>
                      <label className="text-xs text-muted-foreground mb-1 block">{t?.startDate}</label>
                      <input
                        type="date"
                        value={customStartDate}
                        onChange={(e) => setCustomStartDate(e?.target?.value)}
                        className="px-3 py-2 border border-border rounded-md text-sm"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground mb-1 block">{t?.endDate}</label>
                      <input
                        type="date"
                        value={customEndDate}
                        onChange={(e) => setCustomEndDate(e?.target?.value)}
                        className="px-3 py-2 border border-border rounded-md text-sm"
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Analytics Sections */}
            <div className="space-y-6">
              {/* KPI Cards */}
              <KPICards language={language} metrics={metrics} />

              {/* Revenue Trends & Response Metrics */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2">
                  <RevenueTrendsChart 
                    language={language} 
                    supplierId={supplier?.id}
                    startDate={start} 
                    endDate={end} 
                  />
                </div>
                <ResponseMetricsCard language={language} metrics={metrics} />
              </div>

              {/* Order Conversion & Popular Beans */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <OrderConversionChart 
                  language={language}
                  supplierId={supplier?.id}
                  startDate={start}
                  endDate={end}
                />
                <PopularBeansChart 
                  language={language}
                  supplierId={supplier?.id}
                />
              </div>

              {/* Geographic Distribution */}
              <GeographicDistributionMap 
                language={language}
                supplierId={supplier?.id}
              />

              {/* Business Insights */}
              <BusinessInsights 
                language={language}
                metrics={metrics}
                supplierId={supplier?.id}
              />

              {/* Buyer Feedback */}
              <BuyerFeedbackSection 
                language={language}
                supplierId={supplier?.id}
              />
            </div>
          </>
        );
      case 'messages':
        return <MessagesTab />;
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-[60px]">
        <FullWidthSection padding="px-5" py="py-8">
          <ContentWrapper maxWidth="6xl">
          {/* Header Section */}
          <div className="mb-8">
            <div className="flex items-start justify-between">
              <div>
                <h1 className="text-3xl font-heading font-bold text-foreground mb-2">
                  {t?.title}
                </h1>
                <p className="text-muted-foreground">{t?.subtitle}</p>
                <p className="text-sm text-muted-foreground mt-1">
                  {language === 'en' ? supplier?.nameEn : supplier?.nameZh}
                </p>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  iconName="Download"
                  onClick={handleExportCSV}
                  loading={exporting}
                >
                  {t?.exportCSV}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  iconName="FileText"
                  onClick={handleExportPDF}
                  loading={exporting}
                >
                  {t?.exportPDF}
                </Button>
              </div>
            </div>
          </div>

          {/* Tab Navigation */}
          <div className="mb-6 border-b border-border">
            <div className="flex gap-4">
              {tabs?.map(tab => (
                <button
                  key={tab?.id}
                  onClick={() => setActiveTab(tab?.id)}
                  className={`px-4 py-2 font-medium transition-colors ${
                    activeTab === tab?.id ? 'text-primary border-b-2 border-primary' : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <Icon name={tab?.icon} size={16} className="mr-2" />
                  {tab?.label}
                </button>
              ))}
            </div>
          </div>

          {renderTabContent()}
          </ContentWrapper>
        </FullWidthSection>
      </main>
    </div>
  );
};

export default SupplierDashboard;